import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/SupabaseAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pagina per unirsi a un nucleo familiare tramite link/QR
 * URL: /join-family/{famiglia_id}
 */
const JoinFamilyPage = () => {
  const { famigliaId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [familyInfo, setFamilyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Carica info famiglia
  useEffect(() => {
    const loadFamilyInfo = async () => {
      if (!famigliaId) {
        setError('Link non valido');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/famiglia/by-id/${famigliaId}`);
        if (response.ok) {
          const data = await response.json();
          setFamilyInfo(data);
        } else {
          setError('Famiglia non trovata');
        }
      } catch (err) {
        setError('Errore nel caricamento');
      } finally {
        setLoading(false);
      }
    };

    loadFamilyInfo();
  }, [famigliaId]);

  // Unisciti al nucleo
  const handleJoin = async () => {
    if (!user) {
      // Salva l'intent e vai alla registrazione
      localStorage.setItem('join_family_intent', famigliaId);
      navigate('/registrazione');
      return;
    }

    setJoining(true);
    try {
      const response = await fetch(`${API_URL}/api/famiglia/${famigliaId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          email: user.email
        })
      });

      if (response.ok) {
        setSuccess(true);
        toast.success('Benvenuto nel nucleo familiare!');
        
        // Dopo 2 secondi vai alla dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        const error = await response.json();
        if (error.detail?.includes('Limite')) {
          setError('Il nucleo familiare ha raggiunto il limite massimo di membri. Chiedi all\'admin di fare l\'upgrade.');
        } else {
          setError(error.detail || 'Errore nell\'unirsi al nucleo');
        }
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setJoining(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAF9] to-[#E8F5E9] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#4A7C59] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAF9] to-[#E8F5E9] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-[#2C3E50] mb-2">Ops!</h1>
          <p className="text-[#64748B] mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl">
            Torna alla Home
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAF9] to-[#E8F5E9] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#4A7C59]" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50] mb-2">Benvenuto!</h1>
          <p className="text-[#64748B] mb-4">
            Sei entrato nel nucleo familiare di <strong>{familyInfo?.nome_assistito}</strong>
          </p>
          <p className="text-sm text-[#4A7C59]">
            Reindirizzamento alla dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAF9] to-[#E8F5E9] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src="/logo-curacheck.png" 
            alt="CuraCheck" 
            className="h-16 mx-auto mb-4"
          />
        </div>

        {/* Invito */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-[#4A7C59]" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50] mb-2">
            Sei stato invitato!
          </h1>
          <p className="text-[#64748B]">
            Unisciti al nucleo familiare per monitorare
          </p>
          {familyInfo && (
            <p className="text-lg font-semibold text-[#4A7C59] mt-2">
              {familyInfo.nome_assistito}
            </p>
          )}
        </div>

        {/* Info */}
        <div className="bg-[#F9FAFB] rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-[#2C3E50] mb-2">Cosa potrai fare:</h3>
          <ul className="space-y-2 text-sm text-[#64748B]">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#4A7C59]" />
              Ricevere notifiche sulle medicine
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#4A7C59]" />
              Vedere le attività registrate
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#4A7C59]" />
              Monitorare lo stato di salute
            </li>
          </ul>
        </div>

        {/* Azione */}
        {user ? (
          <Button 
            onClick={handleJoin}
            disabled={joining}
            className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
          >
            {joining ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Ingresso in corso...
              </>
            ) : (
              <>
                Unisciti al Nucleo
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        ) : (
          <div className="space-y-3">
            <Button 
              onClick={handleJoin}
              className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
            >
              Registrati e Unisciti
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-center text-sm text-[#64748B]">
              Hai già un account? <a href="/login" className="text-[#4A7C59] font-semibold">Accedi</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinFamilyPage;
